/**
 * Public signup page — Phase 6 Sprint D foundation.
 *
 * Route: /signup
 *
 * Behavior:
 *   - Hits GET /api/auth/signup/status on mount.
 *     - enabled=false → show a "coming soon" placeholder.
 *     - enabled=true  → show the form.
 *   - POST to /api/auth/signup. On 200, show a success card with the
 *     application_id and a clear "pending approval" notice.
 *   - On 429 (rate limit), show the bilingual message + retry seconds.
 *
 * This page does NOT log the user in. The created tenant lives in
 * `tenant_applications` with status=pending_approval until a Super-Admin
 * approves it.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, Lock, CheckCircle2, Loader2, MailPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERTICALS = [
  { id: "beauty_salon",      ar: "صالون تجميل",         en: "Beauty Salon" },
  { id: "spa",               ar: "سبا",                  en: "Spa" },
  { id: "barber",            ar: "حلاق",                 en: "Barber" },
  { id: "nail_salon",        ar: "صالون أظافر",          en: "Nail Salon" },
  { id: "massage_center",    ar: "مركز تدليك",           en: "Massage Center" },
  { id: "wellness_center",   ar: "مركز عافية",           en: "Wellness Center" },
  { id: "gym_fitness",       ar: "صالة رياضية",          en: "Gym / Fitness" },
];

const COUNTRIES = [
  { code: "QA", ar: "قطر",          en: "Qatar" },
  { code: "SA", ar: "السعودية",    en: "Saudi Arabia" },
  { code: "AE", ar: "الإمارات",     en: "UAE" },
  { code: "KW", ar: "الكويت",       en: "Kuwait" },
  { code: "BH", ar: "البحرين",       en: "Bahrain" },
  { code: "OM", ar: "عُمان",         en: "Oman" },
];

export default function Signup() {
  const isAR = (document?.documentElement?.lang || "ar").startsWith("ar");
  const t = (en, ar) => (isAR ? ar : en);

  const [statusLoaded, setStatusLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    business_name_en: "", business_name_ar: "",
    vertical: "beauty_salon", country: "QA",
    phone: "", email: "", password: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    axios.get(`${API}/auth/signup/status`)
      .then((r) => setEnabled(!!r.data?.enabled))
      .catch(() => setEnabled(false))
      .finally(() => setStatusLoaded(true));
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (form.password.length < 8) {
      toast.error(t("Password must be at least 8 characters", "كلمة المرور ٨ خانات على الأقل"));
      return;
    }
    if (!form.business_name_en.trim()) {
      toast.error(t("Business name required", "اسم النشاط مطلوب"));
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/signup`, form);
      setSubmitted(r.data);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data?.detail;
      if (status === 429 && data?.code === "rate_limited") {
        toast.error(
          (isAR ? data.message_ar : data.message_en) +
          ` (${t("retry in", "أعد المحاولة بعد")} ${data.retry_after_seconds}s)`,
          { duration: 6000 }
        );
      } else if (status === 503) {
        setEnabled(false);
      } else if (status === 409) {
        toast.error(typeof data === "string" ? data : t("Already submitted", "تم التسجيل مسبقاً"));
      } else {
        toast.error(t("Submission failed. Please try again.", "تعذّر الإرسال، حاول مجدداً."));
      }
    } finally {
      setBusy(false);
    }
  };

  if (!statusLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-50 to-amber-50">
        <Loader2 className="animate-spin text-stone-400" size={36} />
      </div>
    );
  }

  if (!enabled) {
    return <WaitlistCard isAR={isAR} t={t} />;
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-emerald-50 to-amber-50">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="signup-success">
          <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={44} />
          <h1 className="text-2xl font-bold text-stone-800 mb-2">
            {t("Application received", "تم استلام طلبك")}
          </h1>
          <p className="text-stone-500 leading-relaxed text-sm mb-4">
            {t(
              "Your application is now pending approval. Our team will review it and contact you shortly.",
              "طلبك بانتظار الموافقة. سيراجعه فريقنا ويتواصل معك قريباً."
            )}
          </p>
          <div className="bg-stone-50 rounded-lg p-3 text-xs text-stone-600 mb-4">
            {t("Application ID", "رقم الطلب")}: <code className="text-stone-800 font-mono" data-testid="application-id">{submitted.application_id}</code>
          </div>
          <a href="/" className="inline-block text-[#7A1F3D] hover:underline text-sm">
            ← {t("Back to home", "العودة للرئيسية")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gradient-to-br from-stone-50 to-amber-50" dir={isAR ? "rtl" : "ltr"}>
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8" data-testid="signup-form-card">
        <div className="text-center mb-5">
          <Sparkles className="mx-auto text-[#7A1F3D] mb-2" size={28} />
          <h1 className="text-2xl font-bold text-stone-800">
            {t("Register your salon", "سجّل صالونك")}
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            {t("Get listed on iSaloon. Approval typically within 24h.", "انضم إلى آي صالون. الموافقة عادة خلال ٢٤ ساعة.")}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs text-stone-600 mb-1">{t("Business name (EN)*", "اسم النشاط (إنجليزي)*")}</span>
              <input
                value={form.business_name_en} onChange={set("business_name_en")}
                required minLength={2} maxLength={80}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm"
                data-testid="signup-name-en"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-stone-600 mb-1">{t("Business name (AR)", "اسم النشاط (عربي)")}</span>
              <input
                value={form.business_name_ar} onChange={set("business_name_ar")}
                maxLength={80}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm"
                data-testid="signup-name-ar"
              />
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs text-stone-600 mb-1">{t("Vertical*", "نوع النشاط*")}</span>
              <select
                value={form.vertical} onChange={set("vertical")}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm bg-white"
                data-testid="signup-vertical"
              >
                {VERTICALS.map((v) => (
                  <option key={v.id} value={v.id}>{isAR ? v.ar : v.en}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs text-stone-600 mb-1">{t("Country*", "الدولة*")}</span>
              <select
                value={form.country} onChange={set("country")}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm bg-white"
                data-testid="signup-country"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{isAR ? c.ar : c.en}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="block text-xs text-stone-600 mb-1">{t("Phone*", "رقم الجوال*")}</span>
            <input
              type="tel" value={form.phone} onChange={set("phone")}
              required placeholder="+97455123987"
              className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm" dir="ltr"
              data-testid="signup-phone"
            />
          </label>

          <label className="block">
            <span className="block text-xs text-stone-600 mb-1">{t("Email*", "البريد الإلكتروني*")}</span>
            <input
              type="email" value={form.email} onChange={set("email")}
              required
              className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm" dir="ltr"
              data-testid="signup-email"
            />
          </label>

          <label className="block">
            <span className="block text-xs text-stone-600 mb-1">{t("Password (min 8 chars)*", "كلمة المرور (٨ خانات+)*")}</span>
            <input
              type="password" value={form.password} onChange={set("password")}
              required minLength={8}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm" dir="ltr"
              data-testid="signup-password"
            />
          </label>

          <div className="text-[11px] text-stone-500 leading-relaxed bg-amber-50 border border-amber-100 rounded-lg p-2.5 flex items-start gap-2">
            <ShieldCheck size={13} className="mt-0.5 shrink-0 text-amber-700" />
            {t(
              "Your application will be reviewed by our team before your salon goes live. You won't be able to accept bookings until approval.",
              "سيراجع فريقنا طلبك قبل ظهور صالونك. لن تتمكن من استقبال الحجوزات قبل الموافقة."
            )}
          </div>

          <button
            type="submit" disabled={busy}
            className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="signup-submit"
          >
            {busy && <Loader2 className="animate-spin" size={16} />}
            {t("Submit application", "إرسال الطلب")}
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * Closed-beta waitlist card — shown on /signup when
 * `auth.signup.enabled` is OFF. Captures email (+ optional business
 * name) and stores a `tenant_applications` row with status="waitlisted".
 *
 * Strictly no password, no OTP, no notifications, no automation.
 */
function WaitlistCard({ isAR, t }) {
  const [email, setEmail] = useState("");
  const [biz, setBiz] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error(t("Please enter a valid email", "يرجى إدخال بريد صحيح"));
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/waitlist`, {
        email: email.trim().toLowerCase(),
        business_name: biz.trim(),
      });
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data?.detail;
      if (status === 429 && data?.code === "rate_limited") {
        toast.error(
          (isAR ? data.message_ar : data.message_en) +
          ` (${t("retry in", "أعد المحاولة بعد")} ${data.retry_after_seconds}s)`,
          { duration: 6000 }
        );
      } else {
        toast.error(t("Could not save. Please try again.", "تعذّر الحفظ، حاول مجدداً."));
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-emerald-50 to-amber-50"
        dir={isAR ? "rtl" : "ltr"}
        data-testid="waitlist-success"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={40} />
          <h1 className="text-2xl font-bold text-stone-800 mb-2">
            {t("You're on the waitlist", "تم تسجيلك بالقائمة")}
          </h1>
          <p className="text-stone-500 leading-relaxed text-sm">
            {t(
              "We'll reach out as soon as private beta opens for your area.",
              "سنتواصل معك فور فتح البيتا المغلقة في منطقتك."
            )}
          </p>
          <a href="/" className="inline-block mt-5 text-[#7A1F3D] hover:underline text-sm" data-testid="back-home">
            ← {t("Back to home", "العودة للرئيسية")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-stone-50 to-amber-50"
      dir={isAR ? "rtl" : "ltr"}
      data-testid="signup-disabled"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8" data-testid="waitlist-card">
        <div className="text-center mb-5">
          <Lock className="mx-auto text-[#7A1F3D] mb-2" size={32} />
          <h1 className="text-2xl font-bold text-stone-800">
            {t("Signup coming soon", "التسجيل قريباً")}
          </h1>
          <p className="text-stone-500 text-sm mt-1.5 leading-relaxed">
            {t(
              "Self-service onboarding is in closed beta. Leave your email and we'll contact you when it opens.",
              "التسجيل الذاتي في بيتا مغلقة. اترك بريدك وسنتواصل معك عند فتحه."
            )}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="waitlist-form">
          <label className="block">
            <span className="block text-xs text-stone-600 mb-1">
              {t("Email*", "البريد الإلكتروني*")}
            </span>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              required maxLength={120}
              dir="ltr"
              className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm"
              data-testid="waitlist-email"
            />
          </label>
          <label className="block">
            <span className="block text-xs text-stone-600 mb-1">
              {t("Business name (optional)", "اسم النشاط (اختياري)")}
            </span>
            <input
              type="text" value={biz} onChange={(e) => setBiz(e.target.value)}
              maxLength={80}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm"
              data-testid="waitlist-business"
            />
          </label>
          <button
            type="submit" disabled={busy}
            className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="waitlist-submit"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : <MailPlus size={16} />}
            {t("Join the waitlist", "انضم لقائمة الانتظار")}
          </button>
        </form>

        <a href="/" className="block mt-5 text-center text-[#7A1F3D] hover:underline text-xs" data-testid="back-home">
          ← {t("Back to home", "العودة للرئيسية")}
        </a>
      </div>
    </div>
  );
}

